import { deserialize, DocumentObject } from '@jsonapiworld/jsonapi-serializer';
import { parseISO } from 'date-fns';
import { backendApiGateway } from '~/client/backend';
import { Document } from '~/types/document';
import {
  VATBook,
  VATEntriesFilters,
  VATEntryFixedAssetProposal,
  VATEntryProposal,
  VATEntryProposalOrigin,
  VATEntrySource,
  VATProposalItemType,
  VATRegistrationEntry,
} from '~/types/vat-bookkeeping';
import {
  buildJsonAPIQueryParams,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
  jsonAPISortBuilder,
} from '~/utils/jsonapi-query';

export const fetchVATEntries = async (companyId: string, filters: VATEntriesFilters) => {
  const filtersBuilder = jsonAPIFilterBuilder();
  filtersBuilder.eq('company.id', companyId);

  if (filters.counterpartId) {
    filtersBuilder.eq('counterpartId', filters.counterpartId);
  }

  if (filters.documentDirection) {
    filtersBuilder.eq('document_direction', filters.documentDirection);
  }

  const includeBuilder = jsonAPIIncludeBuilder([
    'vatCode',
    'bookkeepingAccount',
    'vatRegistration.documents.counterpart',
  ]);

  const sortBuilder = jsonAPISortBuilder([{ sort: 'createdAt', direction: 'desc' }]);

  const { data: results } = await backendApiGateway.get<DocumentObject>('/api/v1/vat-entries', {
    params: buildJsonAPIQueryParams(filtersBuilder, includeBuilder, sortBuilder),
  });
  return deserialize<VATRegistrationEntry>(results) as VATRegistrationEntry[];
};

interface VATBookRegistrationProposalResponse {
  id: string;
  name: string;
  type: VATBook['bookType'];
  year: number;
}

type VATEntryProposalRaw = Omit<VATEntryProposal, 'periodStart' | 'periodEnd'> & {
  periodStart?: string | null;
  periodEnd?: string | null;
};

interface VATRegistrationProposalResponse {
  documentId: Document['id'];
  vatEntries: VATEntryProposalRaw[];
  vatBooks: VATBookRegistrationProposalResponse[];
  vatRegistrationDate: string;
  source: VATEntrySource;
}

export const fetchVATRegistrationProposals = async (documentIds: Document['id'][]) => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.in('id', documentIds);

  const params = buildJsonAPIQueryParams(filterBuilder);

  const { data } = await backendApiGateway.get<VATRegistrationProposalResponse[]>(
    '/api/v1/vat-registrations/proposals',
    {
      params,
    },
  );

  const responseMap = new Map(data.map((item) => [item.documentId, item]));

  return documentIds.map((documentId) => {
    const response = responseMap.get(documentId);
    return {
      documentId,
      vatEntries:
        response?.vatEntries.map((entry) => ({
          ...entry,
          periodStart: entry.periodStart ? parseISO(entry.periodStart) : null,
          periodEnd: entry.periodEnd ? parseISO(entry.periodEnd) : null,
        })) ?? [],
      vatBooks:
        response?.vatBooks.map((book) => ({
          id: book.id,
          name: book.name,
          bookType: book.type,
          year: book.year,
        })) ?? [],
      vatRegistrationDate: response?.vatRegistrationDate ? new Date(response.vatRegistrationDate) : undefined,
      source: response?.source,
    };
  });
};

interface AIVATEntryProposalItem {
  lineNumber: number | null;
  itemProposalId: string | null;
  itemType: VATProposalItemType | null;
}

interface AIVATRegistrationProposalResponse {
  documentId: Document['id'];
  vatEntries: (Omit<VATEntryProposal, 'periodStart' | 'periodEnd'> & {
    proposalItems: AIVATEntryProposalItem[];
    origin: VATEntryProposalOrigin | null;
    isFixedAsset: boolean;
    fixedAssets: VATEntryFixedAssetProposal[] | null;
    periodStart?: string | null;
    periodEnd?: string | null;
  })[];
  vatBooks: VATBookRegistrationProposalResponse[];
  vatRegistrationDate: string | null;
  source: VATEntrySource;
}

export const fetchAIVATRegistrationProposals = async (documentIds: Document['id'][]) => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.in('id', documentIds);

  const params = buildJsonAPIQueryParams(filterBuilder);

  const { data } = await backendApiGateway.get<AIVATRegistrationProposalResponse[]>(
    '/api/v1/vat-registrations/ai-proposals',
    {
      params,
    },
  );

  const responseMap = new Map(data.map((item) => [item.documentId, item]));

  return documentIds.map((documentId) => {
    const response = responseMap.get(documentId);
    return {
      documentId,
      vatEntries:
        response?.vatEntries.map((entry) => ({
          ...entry,
          proposalItems: entry.proposalItems,
          origin: entry.origin,
          isFixedAsset: entry.isFixedAsset,
          fixedAssets: entry.fixedAssets,
          periodStart: entry.periodStart ? parseISO(entry.periodStart) : null,
          periodEnd: entry.periodEnd ? parseISO(entry.periodEnd) : null,
        })) ?? [],
      vatBooks:
        response?.vatBooks.map((book) => ({
          id: book.id,
          name: book.name,
          bookType: book.type,
          year: book.year,
        })) ?? [],
      vatRegistrationDate: response?.vatRegistrationDate ? new Date(response.vatRegistrationDate) : undefined,
      source: response?.source,
    };
  });
};
